import { useMemo } from 'react';

import { Text } from '@rbilabs/universal-components';
import { spacing } from '@rbilabs/universal-components/themes';
import { useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { useAcceleratorBadgesOnMenu } from 'pages/menu/hooks/use-accelerator-badges-on-menu';
import { primitive } from 'styles/constants/primitives';

import { MenuAcceleratorCrownSvg } from '../menu-accelerator-badge-svg';
import { BonusType, BonusTypes } from '../types';
import { parseBonusPoints } from '../utils';

interface IMenuAcceleratorPromotionBanner {
  entries: string[];
  isFromCart?: boolean;
}
export const MenuAcceleratorPromotionBanner = ({
  entries,
  isFromCart = false,
}: IMenuAcceleratorPromotionBanner) => {
  const { formatMessage } = useIntl();

  const {
    cartRequirement,
    experimentEnabled,
    bonusPoints,
    pointsMultiplier,
    shouldShowFullMenuBadge,
    promotionId,
  } = useAcceleratorBadgesOnMenu({
    skipExposureLogging: true,
    menuData: [],
  });

  const shouldDisplayPromotionBanner = useMemo(() => {
    if (!experimentEnabled || !entries.length || !promotionId) {
      return false;
    }

    const shouldShowForMatchingEntries = entries.some(entryId => cartRequirement.has(entryId));

    return shouldShowForMatchingEntries || shouldShowFullMenuBadge;
  }, [cartRequirement, entries, experimentEnabled, promotionId, shouldShowFullMenuBadge]);

  if (!shouldDisplayPromotionBanner) {
    return null;
  }

  const bonusType: BonusType = bonusPoints ? BonusTypes.BONUS_POINTS : BonusTypes.POINTS_MULTIPLIER;
  const bonusPointsText = `+${parseBonusPoints(bonusPoints)}`;
  const pointMultiplierText = `${pointsMultiplier}x`;
  const pointsText = bonusType === BonusTypes.BONUS_POINTS ? bonusPointsText : pointMultiplierText;

  const formattedPoints = <Text fontWeight="bold">{pointsText}</Text>;

  const reviewOrderMessage = formatMessage(
    {
      id: 'menuAcceleratorReviewOrderMessage',
      defaultMessage: `Nice pick! You’re getting ${pointsText} points with this order`,
    },
    { points: formattedPoints }
  );

  const cartMessage = formatMessage(
    {
      id: 'menuAcceleratorCartMessage',
      defaultMessage: `Delicious & rewarding — ${pointsText} points coming your way!`,
    },
    { points: formattedPoints }
  );

  return (
    <View style={styles.container}>
      <View style={styles.icon}>
        <MenuAcceleratorCrownSvg />
      </View>
      <View style={styles.message}>
        <Text variant="copyOne">{isFromCart ? cartMessage : reviewOrderMessage}</Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    width: '100%',
    borderRadius: 10,
    padding: 8,
    backgroundColor: primitive.bk.$meltyYellowOpacity20,
    alignItems: 'flex-start',
    gap: spacing.$1,
  },
  icon: { marginTop: spacing['$0.5'], width: 16 },
  message: {
    flex: 1,
  },
});
